import React, { useState, useEffect } from 'react';
import { TextField, Button, Box } from '@mui/material';
import {
  Typography,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

export default function TileModal({
  open,
  columns,
  rowData,
  relatedData,
  handleClose,
  handleSave,
  handleDelete,
  isEditable=true,
}) {
  const [formData, setFormData] = useState(rowData || {});
  const [editErrors, setEditErrors] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Update formData state when rowData changes (e.g., when the modal is opened)
  useEffect(() => {
    if (rowData) {
      setFormData(rowData);
    }
    setEditErrors({});
  }, [rowData, open]);

  const handleChange = (fieldAttr, value) => {
    setFormData({
      ...formData,
      [fieldAttr]: value,
    });

    // Clear error when user edits the field
    setEditErrors((prevErrors) => ({
      ...prevErrors,
      [fieldAttr]: undefined,
    }));
  };

  const handleEditFailure = (errorDict) => {
    console.log(errorDict);
    // Highlight errors
    setEditErrors(errorDict); // Set errors from backend
  };

  const openDeleteConfirmation = () => {
    setIsDeleteModalOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setIsDeleteModalOpen(false);
  };

  const confirmAndDelete = () => {
    handleDelete(rowData); // Call the delete handler passed from the parent component
    closeDeleteConfirmation();
    handleClose(); // Close the edit modal after deletion
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
          }}
        >
          {/* Left side: Title + color box */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h3">
              {isEditable === true ? 'Edit' : 'Details'}
            </Typography>
            <Box
              sx={{
                width: 40,
                height: 20,
                backgroundColor: relatedData.subject?.[formData.subject]?.color || '#f0f0f0',
                borderRadius: 1,
                display: 'flex',
                // marginRight: 1,
                border: '1px solid rgba(0,0,0,0.12)',
                overflow: 'hidden', // Clip inner bar rounding
              }}
            >
            </Box>
          </Box>

          {/* Right side: Close button */}
          {true && ( // ToDo: !isSubmitting
            <IconButton
              edge="end"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <Divider />
        
        <DialogContent>
          <Box
            component="form"
            noValidate
            sx={{
              // display: 'flex',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr', // 2 columns
              mb: 1,
              gap: 2,
              alignItems: 'start',
            }}
          >
            {columns.map((field) =>
              !field.extra && ( // Only render if field.extra is false
                <Box key={field.attr}>
                  <Typography variant="h5" gutterBottom>
                    {field.label}
                  </Typography>
                  {field.type === 'choice' && relatedData[field.attr] ? (
                    <FormControl
                      fullWidth
                      disabled={isEditable === false || field.editable === false}
                      error={Boolean(editErrors[field.attr])}
                    >
                      {/* <InputLabel id={field.attr}>{field.label}</InputLabel> */}
                      <Select
                        // labelId={field.attr}
                        id={field.attr}
                        value={formData[field.attr]}
                        // label={field.label}
                        onChange={(e) => handleChange(field.attr, e.target.value)}
                        fullWidth
                      >
                        {Object.entries(relatedData[field.attr]).map(
                          ([option_key, option_value]) => (
                            <MenuItem key={option_key} value={option_key}>
                              {field.attr === 'subject' ? (
                                relatedData.subject?.[option_key]?.name
                              ) : (
                                option_value.label
                              )}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                      {editErrors[field.attr] && (
                        <FormHelperText>
                          {editErrors[field.attr]?.[0]}
                        </FormHelperText>
                      )}
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      key={field.attr}
                      // label={field.label}
                      name={field.attr}
                      type={field.sub_type || 'text'}
                      value={formData[field.attr] || ''}
                      onChange={(e) => handleChange(field.attr, e.target.value)}
                      required={field.required || false}
                      disabled={isEditable === false || field.editable === false}
                      error={Boolean(editErrors[field.attr])}
                      helperText={editErrors[field.attr]?.[0] || ''}
                    />
                  )}
                </Box>
              ),
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            variant="outlined"
          >
            Cancel
          </Button>
          {/* Conditionally render the Delete button only if handleDelete is provided */}
          { isEditable && handleDelete && (
            <Button
              color="error"
              onClick={openDeleteConfirmation}
              variant="outlined"
            >
              Delete
            </Button>
          )}
          { isEditable && (
            <Button
              onClick={() => handleSave(formData, handleEditFailure)}
              variant="contained"
              color="primary"
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={isDeleteModalOpen}
        onClose={closeDeleteConfirmation}
        onConfirm={confirmAndDelete}
      />
    </>
  );
}
